/** @format */

import { Alert, Button, Descriptions, Modal, Table } from "antd";
import { findValuesFromArray, formatRupiah, uuidv4 } from "utils/helper";
import {
  checkDataErkam,
  checkDataNilai,
  getFilesDokByKode,
  getKodeDokLintasBatch,
  getSumberDanaTahap,
} from "utils/helper/helperBos";
import { ColumnsDokumenMadrasah } from "./ColumnTable";
import { useCallback, useEffect, useMemo } from "react";
import ModalDokumenAction from "components/Modal/ModalActionDokumen";
import { useFormModal } from "hooks/useFormModal";
import { ModalValidationDokumenBos } from "./ModalValidationDokumenBos";
import { useDokumenBos } from "hooks/HookBos/useDokumenBos";
import notifAlert from "components/NotifAlert";
import ModalDoubleDokumenBos from "components/Modal/ModalDoubleDocument";
import { useLaporanMadrasahPengajuanBos } from "hooks/HookBos/useLaporanMadrasahPengajuanBos";
import { ExclamationCircleOutlined } from "@ant-design/icons";

type ModalTypeDok = {
  openModalData: any;
  dataMadrasah: any;
  auth: any;
  tmpSumberDana: any;
  activeTabKey: any;
  dataTableDokumen: any;
  messageInformation: any;
  listJadwal: any;
  activeJadwal: any;
  jadwalPengajuanBos: any;
  handleValidation: (payload: any, type: string) => void;
  handleClose: () => void;
};
export const ModalListDokumenBos = ({
  openModalData,
  dataMadrasah,
  auth,
  tmpSumberDana,
  activeTabKey,
  dataTableDokumen,
  messageInformation,
  listJadwal,
  activeJadwal,
  jadwalPengajuanBos,
  handleValidation,
  handleClose,
}: ModalTypeDok) => {
  const {
    loading,
    nilaiPortalBos,
    nilaiRencanaPendapatan,
    nilaiPengembalianDana,
    dataLaporanErkam,
    getDataBosMadrasah,
  } = useLaporanMadrasahPengajuanBos(dataMadrasah, auth);
  const {
    loadingDokumen,
    filesBos,
    filesDoc,
    fileList,
    getFileList,
    getFiles,
    validasiFileDokumen,
    deleteFileDokumen,
  } = useDokumenBos(dataMadrasah, auth, activeTabKey);

  const ModalFiles = useFormModal();
  const ModalLaporan = useFormModal();
  const ModalDoubleDokumen = useFormModal();
  const listLap = ["docRkam", "docLpj"];
  const showAlert =
    (activeTabKey !== "pm2" &&
      checkDataErkam(dataLaporanErkam, nilaiRencanaPendapatan)) ||
    false;

  const checkJadwal =
    (dataMadrasah?.statusValidasi >= 1 && listJadwal) || activeJadwal;

  const siblingJadwalIds = useMemo(() => {
    return (
      (jadwalPengajuanBos || [])
        .filter(
          (item) =>
            item?.sumberdana === activeTabKey && item?.id !== checkJadwal?.id,
        )
        .map((item) => item?.id) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jadwalPengajuanBos, activeTabKey, checkJadwal]);

  // `kode_id` diketik ulang admin per batch dan tidak stabil, jadi dokumen
  // dicocokkan lewat `nama` — sama seperti sisi Kepala Madrasah di
  // Rencana/PaguDef/Pendapatan/Component/ModalDokumenBos.tsx. Bedanya, di sisi
  // verifikator dokumen APAPUN statusnya (belum disetujui, disetujui, ditolak)
  // harus tetap tampil supaya bisa diverifikasi — fallback ini TIDAK dibatasi
  // statusValidasi.
  const getKodeDokRecord = useCallback(
    (record: any) =>
      getKodeDokLintasBatch(
        dataTableDokumen,
        record,
        siblingJadwalIds,
        dataMadrasah?.penerimaBos,
      ),
    [dataTableDokumen, siblingJadwalIds, dataMadrasah],
  );

  const resolveFileDok = useCallback(
    (record: any) => {
      const tahap = getSumberDanaTahap(activeTabKey);
      const [kodeAktif, ...siblingKodeIds] = getKodeDokRecord(record);
      const penerimaBos = dataMadrasah?.penerimaBos;

      const exact =
        getFilesDokByKode(fileList, [kodeAktif], tahap, penerimaBos)[0] || null;
      if (exact) return exact;

      if (!siblingKodeIds.length) return null;

      return (
        getFilesDokByKode(fileList, siblingKodeIds, tahap, penerimaBos)[0] ||
        null
      );
    },
    [fileList, activeTabKey, dataMadrasah, getKodeDokRecord],
  );

  /** Semua file untuk 1 baris dokumen, termasuk duplikat dari batch lain. */
  const resolveDoubleDok = useCallback(
    (record: any) =>
      getFilesDokByKode(
        fileList,
        getKodeDokRecord(record),
        getSumberDanaTahap(activeTabKey),
        dataMadrasah?.penerimaBos,
      ),
    [fileList, activeTabKey, dataMadrasah, getKodeDokRecord],
  );

  useEffect(() => {
    if (!dataMadrasah) return;
    getDataBosMadrasah();
    getFileList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMadrasah?.madrasahId, dataMadrasah?.nsmMadrasah, auth?.isTahun]);

  const handleAction = (action: any) => {
    if (action === "docRkam") {
      ModalLaporan.openWithInitialData(action);
    } else {
      ModalLaporan.openWithInitialData(action);
    }
  };

  const handleAfterApproval = (record: any) => {
    let namaFile = resolveFileDok(record);

    namaFile.typeDoc =
      record?.typetemplate === "pdf" ? "application/pdf" : record.typetemplate;
    getFiles(namaFile);
    ModalFiles.openWithInitialData(record?.nama);
  };

  const handleValidationFiles = useCallback(
    async (values: any) => {
      const res = await validasiFileDokumen(values);
      if (res) {
        notifAlert({
          type: "success",
          description: `Dokumen Berhasil ${
            (values?.statusValid === 1 && "Disetujui") || "Ditolak"
          }`,
        });
        await getFileList();
        ModalFiles.closeModal();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filesBos, fileList],
  );

  const filteredData = useMemo(() => {
    if (!dataTableDokumen?.length) return [];

    return dataTableDokumen.filter(
      (item) =>
        item.penerima_bos === dataMadrasah?.penerimaBos &&
        item.djadwal_pengajuan_id === checkJadwal?.id,
    );
  }, [dataTableDokumen, dataMadrasah, checkJadwal]);

  const handleDoubleDokumen = (record: any, filesDocDouble: any) => {
    const tmpFileDocDouble = filesDocDouble;
    // filesDocDouble.filter(
    //   (item) => !filteredData.some((iitem) => iitem.kode_id === item.kodeDok),
    // ) || [];
    ModalDoubleDokumen.openWithInitialAll(record, tmpFileDocDouble);
  };

  const handleDeleteFiles = useCallback(
    async (values: any) => {
      const res = await deleteFileDokumen(values);
      if (res) {
        Modal.success({
          title: "Sukses",
          content: "Dokumen berhasil dihapus",
          centered: true,
          wrapClassName: "erkam-custom-modal",
        });
        await getFileList();
        const tmpDoubleDokumen =
          ModalDoubleDokumen.initialListData?.filter(
            (item) => item.id !== values.id,
          ) ?? [];
        ModalDoubleDokumen.setListData(tmpDoubleDokumen);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ModalDoubleDokumen.initialListData,
      ModalDoubleDokumen.setListData,
      deleteFileDokumen,
      getFileList,
    ],
  );

  const columns = useMemo(() => {
    return ColumnsDokumenMadrasah(
      fileList,
      activeTabKey,
      dataMadrasah,
      showAlert,
      listLap,
      handleAfterApproval,
      handleAction,
      handleDoubleDokumen,
      resolveFileDok,
      resolveDoubleDok,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileList,
    activeTabKey,
    dataMadrasah,
    listLap,
    resolveFileDok,
    resolveDoubleDok,
  ]);

  const dataAction = useMemo(() => {
    return {
      tahap: dataMadrasah?.tahap,
      checkDataMadrasah: nilaiPengembalianDana,
      auth: auth,
      dataJadwalBOS: checkJadwal,
      madrasahId: dataMadrasah?.madrasahId,
      status: "s",
    };
  }, [
    dataMadrasah?.tahap,
    dataMadrasah?.madrasahId,
    nilaiPengembalianDana,
    auth,
    checkJadwal,
  ]);

  const tmpFileDocCheck =
    filteredData.map((item) => resolveFileDok(item)).filter(Boolean) || [];

  const verificationButton = () => {
    if (!checkJadwal) return true;

    if (!tmpFileDocCheck?.length) return true;

    if (tmpFileDocCheck.find((item) => item?.statusValidasi.toString() !== "1"))
      return true;
    if (dataMadrasah?.statusValidasi) return true;
    if (showAlert) return true;
    return false;
  };

  const handleConfirmValidation = (type: string) => {
    const payload = (type === "approve" && {
      id: uuidv4(),
      tahun: auth?.isTahun,
      statusPencairan: 1,
      tahap: dataMadrasah?.tahap,
      madrasahId: dataMadrasah?.madrasahId || null,
      nsmMadrasah: dataMadrasah?.nsmMadrasah || null,
      namaMadrasah: dataMadrasah?.namaMadrasah || null,
      nilaiPencairan: checkDataNilai(nilaiPortalBos, activeTabKey),
      noBuktiPencairan: "",
      penerimaBos: dataMadrasah?.penerimaBos,
      jenisMadrasah: dataMadrasah?.jenisMadrasah,
    }) || {
      tahap: dataMadrasah?.tahap,
      tahun: dataMadrasah?.tahun,
      nsm: dataMadrasah?.nsmMadrasah,
      keterangan: null,
      statusPenerimaBos: dataMadrasah?.penerimaBos,
    };
    Modal.confirm({
      title: "Konfirmasi",
      icon: <ExclamationCircleOutlined />,
      content: `Apakah dokumen Dana BOS akan ${(type === "approve" && "disetujui") || "ditolak"}?`,
      okText: "Ya",
      onOk() {
        return handleValidation(payload, type);
      },
      cancelText: "Batal",
    });
  };

  const rejectButton = () => {
    if (dataMadrasah?.statusValidasi) return true;

    if (!checkJadwal) return true;
    if (!tmpFileDocCheck?.length) return true;
    if (
      tmpFileDocCheck.find((item) => item?.statusValidasi?.toString() === "2")
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      <Modal
        open={openModalData}
        width={1200}
        title={`Lihat Dokumen`}
        onCancel={handleClose}
        footer={[
          <Button key="back" onClick={handleClose}>
            Kembali
          </Button>,
          <Button
            key="tolak"
            loading={loading}
            disabled={rejectButton()}
            onClick={() => handleConfirmValidation("reject")}
            danger>
            Batalkan Persetujuan
          </Button>,
          <Button
            key="setuju"
            type="primary"
            loading={loading}
            disabled={verificationButton()}
            onClick={() => handleConfirmValidation("approve")}>
            Disetujui Dokumen
          </Button>,
        ]}>
        <Descriptions className="mb-2 px-6" layout="vertical" bordered>
          <Descriptions.Item label={`Alokasi Dana BOS TA. ${auth?.isTahun}`}>
            <div className="mb-2 text-xl font-bold">
              {formatRupiah(
                nilaiPortalBos?.apbn_bos_semester1 +
                  nilaiPortalBos?.apbn_bos_semester2 +
                  nilaiPortalBos?.apbn_bos_triwulan1 +
                  nilaiPortalBos?.apbn_bos_triwulan2 +
                  nilaiPortalBos?.apbn_bos_triwulan3 +
                  nilaiPortalBos?.apbn_bos_triwulan4,
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item
            label={`Nominal ${findValuesFromArray(
              tmpSumberDana,
              "kode",
              activeTabKey,
              "nama",
            )} TA. ${auth?.isTahun}`}>
            <div className="mb-2 text-xl font-bold">
              {formatRupiah(checkDataNilai(nilaiPortalBos, activeTabKey))}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Jumlah Siswa">
            <div className="mb-2 text-xl font-bold">
              {nilaiPortalBos?.jumlahSiswa} Orang
            </div>
          </Descriptions.Item>
        </Descriptions>
        <Table
          loading={loading}
          rowKey={(record: any) => record?.id}
          columns={columns}
          dataSource={filteredData}
          bordered
          pagination={false}
        />

        <div className="p-4">
          <Alert
            message={
              <div className="p-2">
                <div className="font-bold">{messageInformation}</div>
              </div>
            }
            type="info"
            showIcon
          />
        </div>
      </Modal>
      <ModalValidationDokumenBos
        openModal={ModalFiles.open}
        title={ModalFiles.initialData}
        dataMadrasah={dataMadrasah}
        files={filesBos}
        filesDoc={filesDoc}
        loading={loadingDokumen}
        handleValidation={handleValidationFiles}
        handleClose={ModalFiles.closeModal}
      />
      <ModalDokumenAction
        openModal={ModalLaporan.open}
        dataAction={null}
        dataDokumen={filteredData}
        dataSumber={null}
        dataFile={[]}
        dataBOS={null}
        dataLaporan={dataLaporanErkam}
        dataMadrasah={dataAction}
        showAlert={showAlert}
        onClose={ModalLaporan.closeModal}
        action={ModalLaporan.initialData}
      />
      <ModalDoubleDokumenBos
        fileDoubleDok={ModalDoubleDokumen.initialListData}
        loading={loading}
        openModalDataDouble={ModalDoubleDokumen.open}
        onClose={ModalDoubleDokumen.closeModal}
        onDeleteFile={handleDeleteFiles}
        onPriview={handleAfterApproval}
      />
    </>
  );
};
